import { Suit, Rank, type Card, type TrickCard } from "../gen/bazivan/v1/table_pb";

/** نمادِ خال. همان ترتیبی که سرور می‌فرستد. */
export const suitSymbol: Record<number, string> = {
  [Suit.HEARTS]: "♥",
  [Suit.DIAMONDS]: "♦",
  [Suit.SPADES]: "♠",
  [Suit.CLUBS]: "♣",
};

/** نامِ فارسیِ خال‌ها، همان‌طور که سرِ میز گفته می‌شود. */
export const suitName: Record<number, string> = {
  [Suit.HEARTS]: "دل",
  [Suit.DIAMONDS]: "خشت",
  [Suit.SPADES]: "پیک",
  [Suit.CLUBS]: "گشنیز",
};

export const suits: Suit[] = [Suit.HEARTS, Suit.DIAMONDS, Suit.SPADES, Suit.CLUBS];

/** نامِ گفتنیِ رتبه‌ها — برای صفحه‌خوان، که «K♠» را «کِی» می‌خوانَد نه «شاه». */
const rankSpoken: Record<number, string> = {
  [Rank.ACE]: "آس",
  [Rank.JACK]: "سرباز",
  [Rank.QUEEN]: "بی‌بی",
  [Rank.KING]: "شاه",
};

/** نامِ کارت همان‌طور که سرِ میز گفته می‌شود: «شاهِ دل»، «۷ پیک». */
export function cardName(c: Pick<Card, "rank" | "suit">): string {
  const r = rankSpoken[c.rank] ?? fa(c.rank);
  const s = suitName[c.suit];
  return s ? `${r} ${s}` : r;
}

export function isRed(s: Suit): boolean {
  return s === Suit.HEARTS || s === Suit.DIAMONDS;
}

/** رتبه روی کارت. حروفِ لاتین چون خودِ دسته‌ی ورق همین است. */
export function rankLabel(r: Rank): string {
  switch (r) {
    case Rank.ACE: return "A";
    case Rank.JACK: return "J";
    case Rank.QUEEN: return "Q";
    case Rank.KING: return "K";
    default: return String(r);
  }
}

/** کلیدِ یکتای کارت — برای key در فهرست‌ها و مقایسه. */
export function cardKey(c: Card | undefined): string {
  return c ? `${c.suit}-${c.rank}` : "none";
}

export function sameCard(a: Card | undefined, b: Card | undefined): boolean {
  return !!a && !!b && a.suit === b.suit && a.rank === b.rank;
}

/**
 * مرتب‌کردنِ دست: خال‌به‌خال، و در هر خال از بزرگ به کوچک (آس بالاترین).
 *
 * سرور دست را به ترتیبِ پخش می‌دهد؛ دستِ نامرتب روی صفحه‌ی کوچک واقعاً
 * بازی را سخت می‌کند.
 */
export function sortHand(hand: Card[]): Card[] {
  const order = [Suit.SPADES, Suit.HEARTS, Suit.CLUBS, Suit.DIAMONDS];
  const high = (r: Rank) => (r === Rank.ACE ? 14 : r);
  return [...hand].sort((a, b) => {
    const d = order.indexOf(a.suit) - order.indexOf(b.suit);
    return d !== 0 ? d : high(b.rank) - high(a.rank);
  });
}

/**
 * خالِ زمین — خالی که دستِ جاری با آن شروع شده.
 *
 * سرور کارت‌های روی میز را **به ترتیبِ صندلی** می‌فرستد، نه به ترتیبِ بازی. پس
 * `trick[0]` لزوماً کارتِ شروع‌کننده نیست؛ باید از روی `leader` پیدایش کرد.
 * همین یک اشتباه باعث می‌شد کلاینت خالِ زمین را عوضی بگیرد و همه‌ی کارت‌های
 * دیگر را قفل کند: «نوبتم هست ولی نمی‌توانم کارت انتخاب کنم».
 */
export function leadSuit(trick: TrickCard[], leader: number): Suit | undefined {
  if (trick.length === 0) return undefined;
  return trick.find((t) => t.seat === leader)?.card?.suit;
}

/**
 * آیا این کارت در حکم قابلِ بازی است؟
 *
 * قاعده‌ی «خالِ زمین» را این‌جا هم می‌دانیم، ولی فقط برای کم‌رنگ‌کردنِ کارت‌های
 * غیرمجاز. داورِ واقعی سرور است و کارتِ نامجاز را رد می‌کند؛ این‌جا هیچ‌چیز را
 * تصمیم نمی‌گیریم، فقط بازیکن را از ضربه‌ی بی‌فایده نجات می‌دهیم.
 */
export function hokmPlayable(hand: Card[], lead: Suit | undefined): (c: Card) => boolean {
  if (lead === undefined || lead === Suit.UNSPECIFIED) return () => true;
  const has = hand.some((c) => c.suit === lead);
  return (c) => !has || c.suit === lead;
}

/** رقم‌های فارسی — برای شمارنده‌ها و امتیازها. */
export function fa(n: number | bigint): string {
  return String(n).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
}

/** مقدارِ پولی به فارسی با جداکننده‌ی هزارگان («۴٬۲۰۰») — Intl، نه نگاشتِ دستی.
    `fa` برای شمارش و سال و کد است که جداکننده نمی‌خواهند. */
const money = new Intl.NumberFormat("fa-IR");
export function faNum(n: number | bigint): string {
  return money.format(n);
}
